"use client";

import { Plus, Trash2 } from "lucide-react";
import type { ResumeDocument } from "@/types/cv";
import { CV_BUILDER_EXAMPLE } from "@/components/cv-builder/sample-resume";
import { TextField, TagField } from "@/components/cv-builder/fields";
import { Button } from "@/components/ui/button";

interface StepProps {
  resume: ResumeDocument;
  onChange: (next: ResumeDocument) => void;
}

export function SkillsStep({ resume, onChange }: StepProps) {
  const info = resume.additional_information;
  const patchInfo = (patch: Partial<ResumeDocument["additional_information"]>) =>
    onChange({
      ...resume,
      additional_information: { ...info, ...patch },
    });

  const languages = info.languages;
  const patchLanguage = (
    index: number,
    value: ResumeDocument["additional_information"]["languages"][number],
  ) =>
    patchInfo({
      languages: languages.map((lang, i) => (i === index ? value : lang)),
    });

  return (
    <div className="space-y-5">
      <TagField
        label="Skill teknis"
        helper="Tools, software, atau keahlian. Ketik lalu tekan Enter atau koma."
        tags={info.technical_skills}
        onChange={(technical_skills) => patchInfo({ technical_skills })}
        placeholder={CV_BUILDER_EXAMPLE.technicalSkills}
      />
      <TagField
        label="Soft skill"
        tags={info.soft_skills}
        onChange={(soft_skills) => patchInfo({ soft_skills })}
        placeholder={CV_BUILDER_EXAMPLE.softSkills}
      />
      <div className="space-y-2">
        <p className="text-sm font-medium">Bahasa</p>
        {languages.map((lang, index) => (
          <div key={index} className="flex items-end gap-2">
            <div className="flex-1">
              <TextField
                label="Bahasa"
                value={lang.name}
                onChange={(name) => patchLanguage(index, { ...lang, name })}
                placeholder={CV_BUILDER_EXAMPLE.language}
              />
            </div>
            <div className="flex-1">
              <TextField
                label="Level"
                value={lang.proficiency}
                onChange={(proficiency) =>
                  patchLanguage(index, { ...lang, proficiency })
                }
                placeholder={CV_BUILDER_EXAMPLE.languageLevel}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mb-0.5 shrink-0 text-muted-foreground"
              onClick={() =>
                patchInfo({
                  languages: languages.filter((_, i) => i !== index),
                })
              }
              aria-label="Hapus bahasa"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            patchInfo({
              languages: [...languages, { name: "", proficiency: "" }],
            })
          }
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Tambah bahasa
        </Button>
      </div>
    </div>
  );
}
